import {announce} from '@react-aria/live-announcer'
import {toast} from 'sonner'

import useAccountInformation from '@/auth/hooks/useAccountInformation'
import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import {
  type FoodGrubhubCartItemWithoutQuantity,
  type FoodGrubhubCartItemWithoutQuantityAndMoreInfo,
  FoodGrubhubCartV1,
  getDefaultFoodGrubhubCart,
} from '@/lib/food-grubhub/foodGrubhubCartDb'
import * as foodGrubhubCartOperations from '@/lib/food-grubhub/foodGrubhubCartOperations'
import {
  addToFoodGrubhubCart,
  removeFromFoodGrubhubCart,
  updateFoodGrubhubCartProductQuantity,
} from '@/lib/food-grubhub/foodGrubhubPaymentService'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {useLocale} from '@/lib/locale/locale'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import tryCatch from '@/utils/tryCatch'

import {getFoodGrubhubCartQueryKey} from './useFoodGrubhubCart'
import useGrubhubGetAuth from './useGrubhubGetAuth'

export const FoodGrubhubCartAction = {
  Add: 'Add',
  Remove: 'Remove',
  UpdateQuantity: 'UpdateQuantity',
} as const
export type FoodGrubhubCartAction =
  (typeof FoodGrubhubCartAction)[keyof typeof FoodGrubhubCartAction]

interface AddVariables {
  action: typeof FoodGrubhubCartAction.Add
  data: {
    item: FoodGrubhubCartItemWithoutQuantity
    quantity?: number
  }
}

interface RemoveVariables {
  action: typeof FoodGrubhubCartAction.Remove
  data: {
    item: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
    quantity?: number
  }
}

interface UpdateQuantityVariables {
  action: typeof FoodGrubhubCartAction.UpdateQuantity
  data: {
    item: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
    quantity: number
  }
}

type Variables = AddVariables | RemoveVariables | UpdateQuantityVariables

export default function useFoodGrubhubCartMutation() {
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const isAuthenticated = useIsAuthenticatedValue()
  const [account] = useAccountInformation()
  const {data: authResponse} = useGrubhubGetAuth()
  const latestAccessToken = useLatest(authResponse?.session_handle.access_token)
  const [userLocation] = useGrubhubUserAddress()

  const queryKey = getFoodGrubhubCartQueryKey(locale, account)

  return useMutation({
    mutationFn: async (variables: Variables) => {
      switch (variables.action) {
        case FoodGrubhubCartAction.Add:
          return addToFoodGrubhubCart({
            isAuthenticated,
            item: variables.data.item,
            quantity: variables.data.quantity,
            locale,
            foodToken: latestAccessToken.current ?? '',
            longitude: userLocation.longitude ?? 0,
            latitude: userLocation.latitude ?? 0,
          })
        case FoodGrubhubCartAction.Remove:
          return removeFromFoodGrubhubCart({
            isAuthenticated,
            itemOrIndex: variables.data.item,
            quantity: variables.data.quantity,
            locale,
            foodToken: latestAccessToken.current ?? '',
            longitude: userLocation.longitude ?? 0,
            latitude: userLocation.latitude ?? 0,
          })
        case FoodGrubhubCartAction.UpdateQuantity:
          return updateFoodGrubhubCartProductQuantity({
            isAuthenticated,
            itemOrIndex: variables.data.item,
            quantity: variables.data.quantity,
            locale,
            foodToken: latestAccessToken.current ?? '',
            longitude: userLocation.longitude ?? 0,
            latitude: userLocation.latitude ?? 0,
          })
      }
    },
    onMutate: async variables => {
      await queryClient.cancelQueries({queryKey})

      const [previous = getDefaultFoodGrubhubCart()] = tryCatch(() =>
        FoodGrubhubCartV1(queryClient.getQueryData(queryKey)),
      )

      let newFoodCart

      switch (variables.action) {
        case FoodGrubhubCartAction.Add:
          newFoodCart = foodGrubhubCartOperations.addToFoodGrubhubCart({
            foodGrubhubCart: previous,
            item: variables.data.item,
            quantity: variables.data.quantity,
          })
          break
        case FoodGrubhubCartAction.Remove:
          newFoodCart = foodGrubhubCartOperations.removeFromFoodGrubhubCart({
            foodGrubhubCart: previous,
            itemOrIndex: variables.data.item,
          })
          break
        case FoodGrubhubCartAction.UpdateQuantity:
          newFoodCart = foodGrubhubCartOperations.updateFoodGrubhubCartProductQuantity({
            foodGrubhubCart: previous,
            itemOrIndex: variables.data.item,
            quantity: variables.data.quantity,
          })
          break
      }

      await queryClient.setQueryData(queryKey, newFoodCart)

      return {previous}
    },
    onSuccess: (data, variables) => {
      let message = ''
      // TODO: update message
      switch (variables.action) {
        case FoodGrubhubCartAction.Add:
          message = `Added to cart`
          break
        case FoodGrubhubCartAction.Remove:
          message = `Removed from cart`
          break
        case FoodGrubhubCartAction.UpdateQuantity:
          message = `Updated quantity to ${variables.data.quantity}`
          break
      }

      announce(message)
      toast.success(message)

      queryClient.setQueryData(queryKey, () => data)
    },

    onError: (error, variables, context) => {
      toastErrorMessage(error.message)

      if (context?.previous) {
        queryClient.setQueryData(queryKey, () => context.previous)
      }
    },
  })
}
